/**
 * TextHighlighter - 高性能文本高亮工具类
 *
 * 优化特性：
 * 1. MutationObserver 检测 textLayer 就绪，替代固定超时
 * 2. DocumentFragment 批量 DOM 操作，减少重排
 * 3. 事件委托模式，减少事件监听器数量
 * 4. CSS 变量替代内联样式，提升渲染性能
 */
import { logger } from './logger';
export class TextHighlighter {
  constructor(pageElement, options = {}) {
    this.pageElement = pageElement;
    this.options = {
      highlightColor: 'rgba(255, 235, 59, 0.4)',
      selectedColor: 'rgba(33, 150, 243, 0.3)',
      borderColor: '#FFC107',
      ...options
    };
    this.highlights = new Map();
    this.isSelecting = false;
    this.isProcessing = false;
    this.selectionData = null;
    this.currentTooltip = null;

    // 防抖控制
    this.lastSelectionTime = 0;
    this.lastSelectionText = '';
    this.SELECTION_DEBOUNCE_TIME = 2000;

    // 保存绑定后的函数引用
    this.boundHandleMouseDown = this.handleMouseDown.bind(this);
    this.boundHandleMouseUp = this.handleMouseUp.bind(this);
    this.boundHandleSelectStart = this.handleSelectStart.bind(this);

    // 事件委托的绑定函数
    this.boundContainerMouseEnter = this.handleContainerMouseEnter.bind(this);
    this.boundContainerMouseLeave = this.handleContainerMouseLeave.bind(this);
    this.boundContainerClick = this.handleContainerClick.bind(this);

    this.init();
  }

  /**
   * 静态方法：等待 textLayer 就绪
   * 使用 MutationObserver 替代固定超时，更精确地检测就绪状态
   */
  static waitForTextLayer(pageElement, timeout = 2000) {
    return new Promise((resolve) => {
      // 检查 textLayer 是否已存在
      const checkTextLayer = () => {
        const textLayer = pageElement.querySelector('.react-pdf__Page__textContent') ||
                         pageElement.querySelector('[class*="textLayer"]');
        return textLayer && textLayer.children.length > 0 ? textLayer : null;
      };

      const existingLayer = checkTextLayer();
      if (existingLayer) {
        resolve(existingLayer);
        return;
      }

      // 使用 MutationObserver 监听
      const observer = new MutationObserver(() => {
        const layer = checkTextLayer();
        if (layer) {
          observer.disconnect();
          resolve(layer);
        }
      });

      observer.observe(pageElement, {
        childList: true,
        subtree: true,
        attributes: true
      });

      // 超时保护（降级处理）
      setTimeout(() => {
        observer.disconnect();
        resolve(checkTextLayer()); // 即使没找到也返回，允许降级
      }, timeout);
    });
  }

  init() {
    this.removeEventListeners();
    this.bindEvents();
    this.createHighlightContainer();
    this.injectStyles();
    this.initContainerEvents();
  }

  createHighlightContainer() {
    const existing = this.pageElement.querySelector('.highlight-container');
    if (existing) existing.remove();

    this.highlightContainer = document.createElement('div');
    this.highlightContainer.className = 'highlight-container';
    this.pageElement.appendChild(this.highlightContainer);
  }

  /**
   * 注入优化后的 CSS 样式
   * 使用 CSS 变量实现高性能样式切换
   */
  injectStyles() {
    if (document.querySelector('#highlight-styles-v2')) return;

    const style = document.createElement('style');
    style.id = 'highlight-styles-v2';
    style.textContent = `
      /* ===== 高亮容器 ===== */
      .highlight-container {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 5;
      }

      /* ===== 高亮组（用于事件委托） ===== */
      .permanent-highlight-group {
        pointer-events: auto;
      }

      .permanent-highlight-group:hover .permanent-highlight {
        filter: brightness(1.1);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }

      /* ===== 单个高亮矩形（使用 CSS 变量） ===== */
      .permanent-highlight {
        position: absolute;
        left: var(--hl-left);
        top: var(--hl-top);
        width: var(--hl-width);
        height: var(--hl-height);
        background-color: var(--hl-bg, rgba(255, 235, 59, 0.4));
        border: 1px solid var(--hl-border, #FFC107);
        z-index: 10;
        pointer-events: none;
        transition: filter 0.15s ease, box-shadow 0.15s ease;
        border-radius: 2px;
      }

      /* ===== 临时高亮（选择中） ===== */
      .temporary-highlight {
        position: absolute;
        pointer-events: none;
        z-index: 10;
        animation: highlightPulse 0.4s ease-out;
        border-radius: 2px;
      }

      .temporary-highlight-group {
        pointer-events: none;
      }

      @keyframes highlightPulse {
        0% {
          opacity: 0;
          transform: scale(1.02);
        }
        50% {
          opacity: 0.9;
        }
        100% {
          opacity: 0.7;
          transform: scale(1);
        }
      }

      /* ===== Tooltip 样式 ===== */
      .highlight-tooltip {
        position: fixed;
        background: linear-gradient(135deg, #2d3436 0%, #1e272e 100%);
        color: #fff;
        padding: 10px 14px;
        border-radius: 8px;
        font-size: 13px;
        z-index: 10000;
        pointer-events: none;
        max-width: 280px;
        word-wrap: break-word;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
        border: 1px solid rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(8px);
        animation: tooltipFadeIn 0.15s ease-out;
      }

      .highlight-tooltip::before {
        content: '';
        position: absolute;
        bottom: -6px;
        left: 50%;
        transform: translateX(-50%);
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 6px solid #1e272e;
      }

      .highlight-tooltip-title {
        font-weight: 600;
        margin-bottom: 6px;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        opacity: 0.8;
      }

      .highlight-tooltip-content {
        line-height: 1.4;
      }

      @keyframes tooltipFadeIn {
        from {
          opacity: 0;
          transform: translateY(4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * 事件委托：在容器级别处理所有高亮的交互
   * 大幅减少事件监听器数量
   */
  initContainerEvents() {
    if (!this.highlightContainer) return;

    // 使用捕获阶段确保能捕获到子元素事件
    this.highlightContainer.addEventListener('mouseenter', this.boundContainerMouseEnter, true);
    this.highlightContainer.addEventListener('mouseleave', this.boundContainerMouseLeave, true);
    this.highlightContainer.addEventListener('click', this.boundContainerClick, true);
  }

  handleContainerMouseEnter(e) {
    const group = e.target.closest('.permanent-highlight-group');
    if (!group || !group.dataset.annotation) return;

    try {
      const annotation = JSON.parse(group.dataset.annotation);
      this.showTooltip(e, annotation, group);
    } catch (err) {
      console.warn('解析标注数据失败:', err);
    }
  }

  handleContainerMouseLeave(e) {
    const group = e.target.closest('.permanent-highlight-group');
    if (!group) return;

    // 检查是否真的离开了 group
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && group.contains(relatedTarget)) return;

    this.hideTooltip();
  }

  handleContainerClick(e) {
    const group = e.target.closest('.permanent-highlight-group');
    if (!group || !group.dataset.annotation) return;

    e.stopPropagation();

    try {
      const annotation = JSON.parse(group.dataset.annotation);
      if (typeof this.onHighlightClick === 'function') {
        this.onHighlightClick(annotation);
      }
    } catch (err) {
      console.warn('解析标注数据失败:', err);
    }
  }

  /**
   * 显示 Tooltip（懒加载，使用安全的 DOM 方法）
   */
  showTooltip(event, annotation, groupElement) {
    this.hideTooltip();

    const tooltip = document.createElement('div');
    tooltip.className = 'highlight-tooltip';

    // 使用安全的 DOM 方法替代 innerHTML
    const titleDiv = document.createElement('div');
    titleDiv.className = 'highlight-tooltip-title';
    titleDiv.textContent = this.getTagName(annotation.tag);

    const contentDiv = document.createElement('div');
    contentDiv.className = 'highlight-tooltip-content';
    contentDiv.textContent = annotation.question || annotation.sessionName || '';

    tooltip.appendChild(titleDiv);
    tooltip.appendChild(contentDiv);

    document.body.appendChild(tooltip);

    // 定位到高亮组上方
    const rect = groupElement.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    let left = rect.left + rect.width / 2 - tooltipRect.width / 2;
    let top = rect.top - tooltipRect.height - 10;

    // 边界检测
    if (left < 10) left = 10;
    if (left + tooltipRect.width > window.innerWidth - 10) {
      left = window.innerWidth - tooltipRect.width - 10;
    }
    if (top < 10) {
      top = rect.bottom + 10; // 显示在下方
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;

    this.currentTooltip = tooltip;
  }

  hideTooltip() {
    if (this.currentTooltip) {
      this.currentTooltip.remove();
      this.currentTooltip = null;
    }
  }

  bindEvents() {
    this.pageElement.addEventListener('mousedown', this.boundHandleMouseDown);
    document.addEventListener('mouseup', this.boundHandleMouseUp);
    this.pageElement.addEventListener('selectstart', this.boundHandleSelectStart);
  }

  removeEventListeners() {
    if (this.boundHandleMouseDown) {
      this.pageElement.removeEventListener('mousedown', this.boundHandleMouseDown);
    }
    if (this.boundHandleMouseUp) {
      document.removeEventListener('mouseup', this.boundHandleMouseUp);
    }
    if (this.boundHandleSelectStart) {
      this.pageElement.removeEventListener('selectstart', this.boundHandleSelectStart);
    }

    // 移除容器事件委托
    if (this.highlightContainer) {
      this.highlightContainer.removeEventListener('mouseenter', this.boundContainerMouseEnter, true);
      this.highlightContainer.removeEventListener('mouseleave', this.boundContainerMouseLeave, true);
      this.highlightContainer.removeEventListener('click', this.boundContainerClick, true);
    }
  }

  handleSelectStart(e) {
    if (!e.target || typeof e.target.closest !== 'function') {
      return true;
    }

    const textLayer = e.target.closest('.react-pdf__Page__textContent') ||
                     e.target.closest('[class*="textLayer"]') ||
                     e.target.closest('[class*="TextLayer"]');

    if (!textLayer) {
      this.isSelecting = true;
      return true;
    }

    this.isSelecting = true;
  }

  handleMouseDown(e) {
    const textLayer = e.target.closest('.react-pdf__Page__textContent') ||
                     e.target.closest('[class*="textLayer"]') ||
                     e.target.closest('[class*="TextLayer"]');

    if (!textLayer) {
      this.isSelecting = true;
      this.clearTemporaryHighlight();
      return;
    }

    this.isSelecting = true;
    this.clearTemporaryHighlight();
  }

  handleMouseUp(e) {
    if (!this.isSelecting) return;
    this.isSelecting = false;

    const selection = window.getSelection();
    const selectedText = selection.toString().trim();

    if (selectedText.length < 2) {
      selection.removeAllRanges();
      return;
    }

    const now = Date.now();

    if (this.isProcessing) {
      selection.removeAllRanges();
      return;
    }

    if (
      selectedText === this.lastSelectionText &&
      now - this.lastSelectionTime < this.SELECTION_DEBOUNCE_TIME
    ) {
      selection.removeAllRanges();
      return;
    }

    this.isProcessing = true;
    this.lastSelectionTime = now;
    this.lastSelectionText = selectedText;

    // 使用 requestAnimationFrame 替代 setTimeout，更精确
    requestAnimationFrame(() => {
      this.processSelection();
    });
  }

  processSelection() {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();

    if (selectedText.length < 2) {
      this.isProcessing = false;
      selection.removeAllRanges();
      return;
    }

    const ranges = this.getSelectionRanges(selection);

    if (ranges.length === 0) {
      this.isProcessing = false;
      return;
    }

    this.createTemporaryHighlight(ranges, selectedText);

    if (typeof this.onTextSelected === 'function') {
      // 使用 queueMicrotask 确保 DOM 更新后执行回调
      queueMicrotask(() => {
        this.onTextSelected(selectedText, ranges);

        setTimeout(() => {
          this.isProcessing = false;
        }, 3000);
      });
    } else {
      this.isProcessing = false;
    }
  }

  getSelectionRanges(selection) {
    const ranges = [];

    for (let i = 0; i < selection.rangeCount; i++) {
      const range = selection.getRangeAt(i);
      const rects = this.getRangeRects(range);

      if (rects.length > 0) {
        ranges.push({
          range: range.cloneRange(),
          rects: rects,
          text: range.toString()
        });
      }
    }

    return ranges;
  }

  getRangeRects(range) {
    const rects = [];
    const clientRects = range.getClientRects();
    const pageRect = this.pageElement.getBoundingClientRect();

    for (let rect of clientRects) {
      const relativeRect = {
        left: rect.left - pageRect.left,
        top: rect.top - pageRect.top,
        width: rect.width,
        height: rect.height,
        right: rect.right - pageRect.left,
        bottom: rect.bottom - pageRect.top
      };

      if (relativeRect.width > 2 && relativeRect.height > 2) {
        rects.push(relativeRect);
      }
    }

    return rects;
  }

  createTemporaryHighlight(ranges, text) {
    this.clearTemporaryHighlight();

    const highlightGroup = document.createElement('div');
    highlightGroup.className = 'temporary-highlight-group';
    highlightGroup.dataset.text = text;

    // 使用 DocumentFragment 批量创建
    const fragment = document.createDocumentFragment();

    ranges.forEach(rangeData => {
      rangeData.rects.forEach(rect => {
        const highlight = document.createElement('div');
        highlight.className = 'temporary-highlight';
        highlight.style.cssText = `
          left: ${rect.left}px;
          top: ${rect.top}px;
          width: ${rect.width}px;
          height: ${rect.height}px;
          background-color: ${this.options.selectedColor};
          border: 1px solid ${this.options.borderColor};
        `;
        fragment.appendChild(highlight);
      });
    });

    highlightGroup.appendChild(fragment);
    this.highlightContainer.appendChild(highlightGroup);
  }

  clearTemporaryHighlight() {
    const temp = this.highlightContainer?.querySelector('.temporary-highlight-group');
    if (temp) temp.remove();
  }

  confirmHighlight(highlightId, annotation) {
    const tempGroup = this.highlightContainer?.querySelector('.temporary-highlight-group');
    if (!tempGroup) return;

    const tempHighlights = tempGroup.querySelectorAll('.temporary-highlight');

    // 使用 DocumentFragment 批量转换
    const permanentGroup = document.createElement('div');
    permanentGroup.className = 'permanent-highlight-group';
    permanentGroup.dataset.highlightId = highlightId;
    permanentGroup.dataset.annotation = JSON.stringify(annotation);

    const fragment = document.createDocumentFragment();
    const bgColor = this.getHighlightColor(annotation.tag);
    const borderColor = this.getBorderColor(annotation.tag);

    tempHighlights.forEach(temp => {
      const permanent = document.createElement('div');
      permanent.className = 'permanent-highlight';

      // 使用 CSS 变量
      permanent.style.setProperty('--hl-left', temp.style.left);
      permanent.style.setProperty('--hl-top', temp.style.top);
      permanent.style.setProperty('--hl-width', temp.style.width);
      permanent.style.setProperty('--hl-height', temp.style.height);
      permanent.style.setProperty('--hl-bg', bgColor);
      permanent.style.setProperty('--hl-border', borderColor);

      fragment.appendChild(permanent);
    });

    permanentGroup.appendChild(fragment);
    this.highlightContainer.appendChild(permanentGroup);

    this.highlights.set(highlightId, {
      element: permanentGroup,
      annotation: annotation
    });

    this.clearTemporaryHighlight();
    window.getSelection().removeAllRanges();
  }

  getTagName(tag) {
    const tagNames = {
      important: '重要',
      question: '疑问',
      summary: '总结',
      note: '笔记',
      idea: '想法'
    };
    return tagNames[tag] || '笔记';
  }

  getHighlightColor(tag) {
    const colors = {
      important: 'rgba(255, 87, 34, 0.35)',
      question: 'rgba(33, 150, 243, 0.35)',
      summary: 'rgba(76, 175, 80, 0.35)',
      note: 'rgba(255, 193, 7, 0.35)',
      idea: 'rgba(156, 39, 176, 0.35)'
    };
    return colors[tag] || colors.note;
  }

  getBorderColor(tag) {
    const colors = {
      important: '#FF5722',
      question: '#2196F3',
      summary: '#4CAF50',
      note: '#FFC107',
      idea: '#9C27B0'
    };
    return colors[tag] || colors.note;
  }

  removeHighlight(highlightId) {
    const highlight = this.highlights.get(highlightId);
    if (highlight) {
      highlight.element.remove();
      this.highlights.delete(highlightId);
    }
  }

  clearAllHighlights() {
    if (this.highlightContainer) {
      this.highlightContainer.textContent = '';
    }
    this.highlights.clear();
  }

  /**
   * 批量恢复高亮（性能优化版）
   * 使用 DocumentFragment 批量插入
   */
  restoreHighlights(savedHighlights) {
    logger.log('🔄 restoreHighlights 开始', {
      数量: savedHighlights?.length || 0,
      container存在: !!this.highlightContainer
    });

    if (!savedHighlights || savedHighlights.length === 0) return;
    if (!this.highlightContainer) {
      console.warn('⚠️ highlightContainer 不存在，跳过恢复');
      return;
    }

    this.clearAllHighlights();

    const fragment = document.createDocumentFragment();

    savedHighlights.forEach((annotation) => {
      if (!annotation.highlightRects || annotation.highlightRects.length === 0) {
        return;
      }

      const group = this.createHighlightGroup(annotation);
      if (group) {
        fragment.appendChild(group);
        this.highlights.set(annotation.id, {
          element: group,
          annotation: annotation
        });
      }
    });

    // 一次性插入所有高亮
    this.highlightContainer.appendChild(fragment);
    logger.log('✅ restoreHighlights 完成，当前高亮数:', this.highlights.size);
  }

  /**
   * 创建单个高亮组（内部方法）
   */
  createHighlightGroup(annotation) {
    const group = document.createElement('div');
    group.className = 'permanent-highlight-group';
    group.dataset.highlightId = annotation.id;
    group.dataset.annotation = JSON.stringify(annotation);

    const fragment = document.createDocumentFragment();
    const bgColor = this.getHighlightColor(annotation.tag);
    const borderColor = this.getBorderColor(annotation.tag);

    annotation.highlightRects.forEach((rectData) => {
      const highlight = document.createElement('div');
      highlight.className = 'permanent-highlight';

      // 使用 CSS 变量替代内联样式
      highlight.style.setProperty('--hl-left', `${rectData.left}px`);
      highlight.style.setProperty('--hl-top', `${rectData.top}px`);
      highlight.style.setProperty('--hl-width', `${rectData.width}px`);
      highlight.style.setProperty('--hl-height', `${rectData.height}px`);
      highlight.style.setProperty('--hl-bg', bgColor);
      highlight.style.setProperty('--hl-border', borderColor);

      fragment.appendChild(highlight);
    });

    group.appendChild(fragment);
    return group;
  }

  /**
   * 恢复单个高亮（兼容旧接口）
   */
  restoreHighlight(annotation) {
    if (!annotation.highlightRects || annotation.highlightRects.length === 0) {
      return;
    }

    const group = this.createHighlightGroup(annotation);
    if (group) {
      this.highlightContainer.appendChild(group);
      this.highlights.set(annotation.id, {
        element: group,
        annotation: annotation
      });
    }
  }

  onTextSelected(text, ranges) {
    logger.log('Text selected:', text, ranges);
  }

  onHighlightClick(annotation) {
    logger.log('Highlight clicked:', annotation);
  }

  destroy() {
    this.hideTooltip();
    this.isProcessing = false;
    this.lastSelectionTime = 0;
    this.lastSelectionText = '';

    this.removeEventListeners();

    if (this.highlightContainer) {
      this.highlightContainer.remove();
    }

    // 移除旧样式（保留新样式供其他实例使用）
    const oldStyles = document.querySelector('#highlight-styles');
    if (oldStyles) oldStyles.remove();

    this.highlights.clear();
  }
}
